import { useNavigate } from "react-router-dom";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { plural } from "pluralize";
import {
  PropertyGroupSplitBy,
  PropertyGroupStep
} from "views/property-groups-configuration/types";
import { propertyGroupTitleMap } from "../constants";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useMemo } from "react";

const getCrumbTitle = (
  currentStep: PropertyGroupSplitBy,
  previousStep?: PropertyGroupSplitBy,
  dictionary?: { properties: string }
) => {
  if (!previousStep)
    return `Split all ${dictionary?.properties || "properties"} by ${propertyGroupTitleMap[currentStep]}`;

  return `Split ${plural(
    propertyGroupTitleMap[previousStep]
  )} by ${propertyGroupTitleMap[currentStep]}`;
};

export const LayoutHeader = ({
  currentStep,
  selectedSplitOptions,
  isEditMode
}: {
  currentStep: PropertyGroupStep;
  selectedSplitOptions: PropertyGroupSplitBy[];
  isEditMode: boolean;
}) => {
  const navigate = useNavigate();
  const dictionary = useUnitsPropertiesDictionary();

  const crumbs = useMemo(() => {
    const result: {
      title: string;
      href?: string;
      isActive?: boolean;
      onClick?: () => void;
    }[] = [
      {
        title: `${dictionary.Property} groups`,
        onClick: () => navigate("/property-groups")
      }
    ];

    if (!isEditMode)
      result.push({
        title: "Initial configuration",
        onClick: () => navigate("/property-groups/configuration")
      });

    if (currentStep === PropertyGroupStep.INITIAL_CONFIGURATION) {
      result[1].isActive = true;
      return result;
    }

    const stepIndex =
      currentStep === PropertyGroupStep.CONFIRM_GROUPS
        ? selectedSplitOptions.length - 1
        : selectedSplitOptions.indexOf(
            currentStep as unknown as PropertyGroupSplitBy
          );

    selectedSplitOptions.slice(0, stepIndex + 1).forEach((option, index) => {
      result.push({
        title: getCrumbTitle(
          option,
          selectedSplitOptions[index - 1],
          dictionary
        ),
        onClick: () => navigate(`/property-groups/configuration?step=${option}`)
      });
    });

    if (currentStep === PropertyGroupStep.CONFIRM_GROUPS)
      result.push({
        title: "Confirm groups",
        onClick: () => navigate("/property-groups/configuration?step=confirm")
      });

    result[result.length - 1].isActive = true;

    return result;
  }, [currentStep, selectedSplitOptions, dictionary, navigate]);

  return <BreadCrumbs crumbs={crumbs} />;
};
